import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

export default function DNAParticles() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const W = mount.clientWidth;
    const H = mount.clientHeight;

    // Scene
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, W / H, 0.1, 1000);
    camera.position.z = 30;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    // DNA Helix particles
    const particleCount = 800;
    const positions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);
    const sizes = new Float32Array(particleCount);

    const color1 = new THREE.Color('#00AEEF');
    const color2 = new THREE.Color('#0066AA');
    const color3 = new THREE.Color('#8FA8C8');

    for (let i = 0; i < particleCount; i++) {
      const t = (i / particleCount) * Math.PI * 12 - Math.PI * 6;
      const radius = 6;
      const strand = i % 2 === 0 ? 1 : -1;

      if (i % 20 < 2) {
        // Rungs connecting the two strands
        const rungT = Math.floor(i / 20) * (Math.PI * 12 / (particleCount / 20));
        const frac = (i % 2) / 1;
        positions[i * 3] = (frac * 2 - 1) * radius * 0.8 * Math.cos(rungT);
        positions[i * 3 + 1] = rungT * 1.2 - 8;
        positions[i * 3 + 2] = (frac * 2 - 1) * radius * 0.8 * Math.sin(rungT);
        color3.toArray(colors, i * 3);
        sizes[i] = 1.5;
      } else {
        // Helix strands
        positions[i * 3] = radius * Math.cos(t + strand * Math.PI / 2);
        positions[i * 3 + 1] = t * 1.2;
        positions[i * 3 + 2] = radius * Math.sin(t + strand * Math.PI / 2);
        const c = i % 2 === 0 ? color1 : color2;
        c.toArray(colors, i * 3);
        sizes[i] = 2 + Math.random() * 2;
      }
    }

    // Background scatter particles
    const bgCount = 400;
    const bgPositions = new Float32Array(bgCount * 3);
    const bgColors = new Float32Array(bgCount * 3);
    const bgSizes = new Float32Array(bgCount);
    const bgColor = new THREE.Color('#1a3050');

    for (let i = 0; i < bgCount; i++) {
      bgPositions[i * 3] = (Math.random() - 0.5) * 80;
      bgPositions[i * 3 + 1] = (Math.random() - 0.5) * 60;
      bgPositions[i * 3 + 2] = (Math.random() - 0.5) * 40 - 20;
      bgColor.toArray(bgColors, i * 3);
      bgSizes[i] = 0.5 + Math.random() * 1.5;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

    const bgGeometry = new THREE.BufferGeometry();
    bgGeometry.setAttribute('position', new THREE.BufferAttribute(bgPositions, 3));
    bgGeometry.setAttribute('color', new THREE.BufferAttribute(bgColors, 3));
    bgGeometry.setAttribute('size', new THREE.BufferAttribute(bgSizes, 1));

    const mat = new THREE.PointsMaterial({
      size: 0.3, vertexColors: true, transparent: true, opacity: 0.85,
      sizeAttenuation: true, blending: THREE.AdditiveBlending, depthWrite: false,
    });

    const bgMat = new THREE.PointsMaterial({
      size: 0.2, vertexColors: true, transparent: true, opacity: 0.4,
      sizeAttenuation: true, blending: THREE.AdditiveBlending, depthWrite: false,
    });

    const particles = new THREE.Points(geometry, mat);
    const bgParticles = new THREE.Points(bgGeometry, bgMat);

    // Position DNA helix to the right side
    particles.position.x = 8;
    scene.add(bgParticles);
    scene.add(particles);

    // Mouse tracking
    let mouseX = 0, mouseY = 0;
    const onMouseMove = (e) => {
      mouseX = (e.clientX / window.innerWidth - 0.5) * 0.3;
      mouseY = (e.clientY / window.innerHeight - 0.5) * 0.2;
    };
    window.addEventListener('mousemove', onMouseMove);

    // Animation
    let frameId;
    let t = 0;
    const animate = () => {
      frameId = requestAnimationFrame(animate);
      t += 0.004;
      particles.rotation.y = t * 0.3 + mouseX;
      particles.rotation.x = mouseY * 0.5;
      bgParticles.rotation.y = t * 0.05;
      camera.position.x += (mouseX * 5 - camera.position.x) * 0.03;
      renderer.render(scene, camera);
    };
    animate();

    // Resize
    const onResize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
    };
  }, []);

  return (
    <div ref={mountRef} style={{
      position: 'absolute', inset: 0, zIndex: 0,
      opacity: 0.9,
    }} />
  );
}
